/**
 * Super Admin Security Settings Page - 비밀번호 변경
 */

import { useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Eye, EyeOff, Shield } from 'lucide-react';
import { getAdminFunc } from '@/services/SuperAdminFirebase';

export default function SuperAdminSecurityPage() {
    const { i18n } = useLingui();

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState(false);

    const handleChangePassword = async () => {
        setPasswordError('');
        setPasswordSuccess(false);

        if (!currentPassword) {
            setPasswordError(t(i18n)`現在のパスワードを入力してください`);
            return;
        }
        if (!newPassword) {
            setPasswordError(t(i18n)`新しいパスワードを入力してください`);
            return;
        }
        if (newPassword.length < 8) {
            setPasswordError(t(i18n)`パスワードは8文字以上である必要があります`);
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordError(t(i18n)`パスワードが一致しません`);
            return;
        }

        setChangingPassword(true);
        try {
            const changePassword = getAdminFunc('changeSuperAdminPassword');
            await changePassword({
                currentPassword,
                newPassword,
            });
            setPasswordSuccess(true);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setTimeout(() => setPasswordSuccess(false), 3000);
        } catch (e: any) {
            console.error('Failed to change password:', e);
            setPasswordError(e.message || t(i18n)`パスワードの変更に失敗しました`);
        } finally {
            setChangingPassword(false);
        }
    };

    return (
        <div className="container mx-auto p-6 max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
                <Shield className="h-8 w-8" />
                <div>
                    <h1 className="text-2xl font-bold"><Trans>セキュリティ設定</Trans></h1>
                    <p className="text-muted-foreground"><Trans>パスワード変更・アカウントセキュリティ</Trans></p>
                </div>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Lock className="h-5 w-5" />
                        <Trans>パスワード変更</Trans>
                    </CardTitle>
                    <CardDescription>
                        <Trans>Super Admin アカウントのパスワードを変更します。</Trans>
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2 md:col-span-2">
                            <Label htmlFor="current-password"><Trans>現在のパスワード</Trans></Label>
                            <div className="relative">
                                <Input
                                    id="current-password"
                                    type={showCurrentPassword ? 'text' : 'password'}
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                >
                                    {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="new-password"><Trans>新しいパスワード</Trans></Label>
                            <div className="relative">
                                <Input
                                    id="new-password"
                                    type={showNewPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                >
                                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                <Trans>8文字以上</Trans>
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirm-password"><Trans>パスワード確認</Trans></Label>
                            <Input
                                id="confirm-password"
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="••••••••"
                            />
                        </div>
                    </div>

                    {passwordError && (
                        <p className="text-sm text-red-500">{passwordError}</p>
                    )}
                    {passwordSuccess && (
                        <p className="text-sm text-green-600"><Trans>パスワードを変更しました</Trans></p>
                    )}

                    <div className="flex justify-end pt-4 border-t">
                        <Button
                            onClick={handleChangePassword}
                            disabled={changingPassword}
                        >
                            {changingPassword ? (
                                <span className="animate-spin mr-2">⏳</span>
                            ) : (
                                <Lock className="mr-2 h-4 w-4" />
                            )}
                            <Trans>パスワードを変更</Trans>
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
